import React, { useEffect, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { cn } from "@/lib/utils";
import { isReadModeOn, setReadMode } from "@/lib/tapToHear";
import { speak } from "@/lib/speech";

/**
 * <ReadModeToggle /> — the "🔊 Read Mode" pill.
 *
 * Toggling ON turns the whole app into a tap-to-hear experience: any tap
 * on a button/link/heading speaks its label. Turning OFF stops silently.
 *
 * The toggle is intentionally small and lives in the drawer / dropdown
 * alongside High Contrast and Big Text — a family of accessibility
 * controls a parent enables once for a pre-reader.
 */
export default function ReadModeToggle({ variant = "chip" }) {
  const [on, setOn] = useState(() => isReadModeOn());
  useEffect(() => {
    const onChange = (e) => setOn(!!e?.detail?.on);
    window.addEventListener("we-read-mode-changed", onChange);
    return () => window.removeEventListener("we-read-mode-changed", onChange);
  }, []);
  const flip = () => {
    const next = !on;
    setReadMode(next);
    if (next) {
      speak("Read mode is on. Tap anything to hear it.", { voice: "nova" });
    }
  };
  const isCompact = variant === "compact";
  return (
    <button
      type="button"
      onClick={flip}
      data-testid="read-mode-toggle"
      aria-pressed={on}
      title={on ? "Read Mode on — tap anything to hear it" : "Turn Read Mode on"}
      className={cn(
        "inline-flex items-center rounded-full border-[3px] border-slate-900 font-bold shadow-[0_2px_0_0_rgba(15,23,42,1)] transition-all shrink-0",
        isCompact ? "h-11 w-11 justify-center" : (variant === "chip" ? "h-9 px-3 gap-2 text-sm" : "h-11 px-4 gap-2 text-sm"),
        on ? "bg-emerald-300 text-slate-900" : "bg-white text-slate-900 hover:bg-yellow-100",
      )}
    >
      {on ? <Volume2 size={16} strokeWidth={3} /> : <VolumeX size={16} strokeWidth={3} />}
      {!isCompact && <span>Read Mode</span>}
      {on && !isCompact && <span aria-hidden className="text-[10px] font-black uppercase tracking-widest">on</span>}
    </button>
  );
}
